//framework
import React, { useState } from "react";
import PropTypes from "prop-types";

//styles
import styles from "./GameCityLoading.module.scss";

//assets — imported directly (not via IMAGES/Preloader): this scene renders
//before/while preloading runs, so it relies on webpack-bundled URLs instead.
import logoWhite from "images/Shared/GameCity/logo-white.png";

/**
 * Full-screen Game City branded loading scene: purple night sky, stars,
 * clouds, white GC logo, status text with pulsing dots, cityscape silhouette
 * and — only when the admin loading-config supplies one — a promo billboard.
 * Purely presentational — parents (Preloader, ConnectingOverlay) own
 * show/hide and fade logic.
 *
 * Porting to another game:
 * 1. Copy this folder + assets/images/Shared/GameCity/ (4 images) +
 *    services/loadingConfig.js (admin-configurable billboard; only its
 *    GAME_ID import is game-specific).
 * 2. Add the Rubik Google Fonts <link> to public/index.html.
 * 3. The parent container MUST size itself to the visible viewport
 *    (height: 100dvh with a 100vh fallback — see Preloader.module.scss),
 *    or bottom-anchored elements will sit under mobile browser chrome.
 * 4. If the game sets a global font via a universal selector (*), the
 *    `.status *` rule here keeps Rubik winning — leave it in place.
 */
// `large` is accepted for API compatibility but no longer changes sizing —
// text is one consistent size on every loading screen.
export default function GameCityLoading({ text = "Connecting you to the game", showBillboard = true, billboardSrc = null, large = false, className = "" }) { // eslint-disable-line no-unused-vars
    // A billboard URL that fails to load (offline, deleted blob) is hidden
    // rather than swapped — there is deliberately no bundled fallback image.
    const [erroredSrc, setErroredSrc] = useState(null);
    return (
        <div className={`${styles.scene} ${className}`}>
            <div className={styles.stars} />
            <div className={styles.clouds} />
            <div className={styles.topGradient} />
            <div className={styles.content}>
                <img src={logoWhite} alt="Game City" className={styles.logo} draggable={false} />
                <div className={styles.status}>
                    <span className={styles.text}>{text}</span>
                    <span className={styles.dotPulse} aria-hidden="true" />
                </div>
                {showBillboard && billboardSrc && billboardSrc !== erroredSrc && (
                    <img
                        src={billboardSrc}
                        alt=""
                        className={styles.billboard}
                        draggable={false}
                        onError={() => setErroredSrc(billboardSrc)}
                    />
                )}
            </div>
            <div className={styles.cityscape} />
        </div>
    );
}

GameCityLoading.propTypes = {
    text: PropTypes.string,
    showBillboard: PropTypes.bool,
    billboardSrc: PropTypes.string,
    large: PropTypes.bool,
    className: PropTypes.string,
};
